import type { OnInit } from '@angular/core';
import { Component, Inject } from '@angular/core';
import type { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DialogComponent } from '@shared/dialogs/dialog.component';
import { ShiftFetcher } from '@shared/modules/shifts/shift.fetcher';
import { ShiftPresenter } from '@shared/modules/shifts/shift.presenter';
import { AppErrorHandler } from '@shared/services/error-handler/app-error-handler.service';
import { AppSnackBar } from '@shared/services/snackbar/app-snackbar.service';
import { toDateTime } from '@shared/utils/date-time';
import type { DateTime } from 'luxon';
import type { Shift } from '../shift';

@Component({
    selector: 'app-shift-preview-dialog',
    templateUrl: './shift-preview-dialog.component.html',
    styleUrls: ['./shift-preview-dialog.component.scss'],
})
export class ShiftPreviewDialogComponent extends DialogComponent implements OnInit {
    shiftCopy: UpdateShift;

    public shift: Shift;

    public ui = {
        changed: {
            notes: false,
            carer_notes: false,
            start: false,
            duration: false,
            attended: false,

            reset() {
                this.notes = false;
                this.carer_notes = false;
                this.start = false;
                this.duration = false;
                this.attended = false;
            },
        },

        get hasChanged() {
            return this.changed.notes || this.changed.carer_notes || this.changed.start || this.changed.duration || this.changed.attended;
        },

        // Network
        saving: false,

        get loading() {
            return this.saving;
        },
    };

    public state = {
        fetching: false,
        markingAsAttended: false,

        get loading() {
            return this.fetching || this.markingAsAttended;
        }
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ShiftPreviewDialogData,
        private errorHandler: AppErrorHandler,
        private shiftFetcher: ShiftFetcher,
        private snackbar: AppSnackBar,
        public dialogRef: MatDialogRef<ShiftPreviewDialogComponent>,
    ) {
        super();

        this.shift = ShiftPresenter.attach(data.shift);

        this.shiftCopy = {
            start: toDateTime(this.shift.start),
            duration: this.shift.duration,
            notes: this.shift.notes,
            carer_notes: this.shift.carer_notes,
            attended: this.shift.attended
        };
    }

    ngOnInit() {
        this._fetchShift();
    }

    onChange() {
        if (!this.shift || !this.shiftCopy) {
            return;
        }

        this.ui.changed.start = !toDateTime(this.shift.start).equals(this.shiftCopy.start);
        this.ui.changed.duration = this.shift.duration !== this.shiftCopy.duration;
        this.ui.changed.notes = this.shift.notes !== this.shiftCopy.notes;
        this.ui.changed.carer_notes = this.shift.carer_notes !== this.shiftCopy.carer_notes;
        this.ui.changed.attended = this.shift.attended !== this.shiftCopy.attended;
    }

    refresh() {
        this._fetchShift();
    }

    _fetchShift() {
        this.state.fetching = true;

        this.shiftFetcher
            .get(this.shift.id, {
                include: ['carer', 'carer.person:fields(simple)', 'contract', 'restrictions', 'team'],
            })
            .pipe(
                this.takeUntilDestroyed(),
                this.errorHandler.catchError(),
            )
            .subscribe(shift => {
                this.shift = ShiftPresenter.attach(shift);
                this.state.fetching = false;
            }, () => this.state.fetching = false);
    }

    updateAttended(value: boolean) {
        this.shiftCopy.attended = value;
        this.onChange();
    }

    save() {
        if (this.ui.saving) {
            return Promise.reject('Already saving');
        }

        if (!this.shift.id) {
            return Promise.reject('Missing shift ID');
        }

        if (!this.ui.hasChanged) {
            return Promise.reject('Nothing has changed');
        }

        const payload = {
            notes: this.shiftCopy.notes,
            carer_notes: this.shiftCopy.carer_notes,
            start: this.shiftCopy.start,
            duration: this.shiftCopy.duration,
            attended: this.shiftCopy.attended
        };

        const snackbar = this.snackbar.showProgress('Saving');

        this.ui.saving = true;

        this.shiftFetcher.patch(this.shift.id, payload).subscribe(
            shift => {
                this.shift.notes = shift.notes;
                this.shift.carer_notes = shift.carer_notes;
                this.shift.start = shift.start;
                this.shift.duration = shift.duration;

                this.shift.presenter.invalidateCache();

                this.ui.changed.reset();

                snackbar.dismiss();

                this.snackbar.showSuccess('Saved');

                if (shift) {
                    this.dialogRef.close(true);
                }
            },
            this.errorHandler.handle,
            () => {
                this.ui.saving = false;
            },
        );
    }

    public static show(dialog: MatDialog, data: ShiftPreviewDialogData) {
        return dialog.open(ShiftPreviewDialogComponent, { data, maxWidth: '1000px', width: '800px' });
    }
}

export interface ShiftPreviewDialogData {
    shift: Shift;
}

interface UpdateShift {
    notes: string;
    carer_notes: string;
    start: DateTime;
    duration: number;
    attended?: boolean;
}
